import { useEffect, useState } from "react";
import styles from "./Settings.module.scss";
import { SettingsService } from "../../services/settings.service";
import {
  languagesDataType,
  settingsDataType,
  translationsDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { Empty, Modal, Popconfirm, message } from "antd";
import { useFormik } from "formik";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import Spinner from "../../components/shared/Spinner/Spinner";
import MainButton from "../../components/shared/MainButton/MainButton";
import { FaPlus } from "react-icons/fa";
import { LanguageService } from "../../services/languages.service";

const isAddressKey = (key: string) => /ünvan/i.test(key);

const Settings = () => {
  type SettingsRow = settingsDataType & { originalValue?: string };

  const [settings, setSettings] = useState<SettingsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [langs, setLangs] = useState<string[]>(["az", "en", "ru", "uz"]);
  const [langCode, setLangCode] = useState<string>("uz");
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  // Address modal states
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [activeSetting, setActiveSetting] = useState<settingsDataType | null>(
    null,
  );
  const [translations, setTranslations] = useState<translationsDataType[]>([]);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      SettingsService.getAllSettings(),
      LanguageService.getAllLanguages(),
    ])
      .then(([settingsRes, langRes]) => {
        if (Array.isArray(settingsRes?.data)) {
          setSettings(
            settingsRes.data.map((item: settingsDataType) => ({
              ...item,
              originalValue: item.value,
            })),
          );
        }

        if (Array.isArray(langRes?.data)) {
          const visibleLangs = (langRes.data as languagesDataType[])
            .filter((l) => l.is_visible)
            .map((l) => l.code)
            .filter(Boolean);
          if (visibleLangs.length) setLangs(visibleLangs);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const { handleSubmit, handleChange, values, resetForm, touched, errors } =
    useFormik({
      initialValues: {
        key: "",
        value: "",
      },
      validationSchema: Yup.object({
        key: Yup.string().required("Parametrin adı tələb olunur."),
        value: Yup.string().required("Parametrin dəyəri tələb olunur."),
      }),
      onSubmit: async (formValues) => {
        const response = await SettingsService.addSettings({
          key: formValues.key,
          value: formValues.value,
        });
        if (response?.data) {
          const created = response?.data?.data || response?.data;
          setSettings((prev) => [
            ...prev,
            { ...(created as settingsDataType), originalValue: created?.value },
          ]);
          resetForm();
          setIsModalVisible(false);
          message.success("Parametr uğurla əlavə edildi!");
        } else {
          message.error(response.errorMessage || "Xəta baş verdi");
        }
      },
    });

  const handleAddClick = () => {
    setIsModalVisible(true);
  };

  const handleInputChange = (id: number, value: string) => {
    setSettings((prev) =>
      prev.map((item) => (item.id === id ? { ...item, value } : item)),
    );
  };

  const openAddressModal = (setting: settingsDataType) => {
    setActiveSetting(setting);
    setTranslations(setting.translations || []);
    const nextDefault =
      langs.find((c) => c.toLowerCase() === "uz") || langs[0] || "uz";
    setLangCode(nextDefault);
    setAddressModalOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!activeSetting) return;

    const response = await SettingsService.updateSettingById(activeSetting.id, {
      key: activeSetting.key,
      translations,
    });

    if (response.status === 200) {
      message.success("Ünvan uğurla yeniləndi");

      setSettings((prev) =>
        prev.map((item) =>
          item.id === activeSetting.id ? { ...item, translations } : item,
        ),
      );

      setAddressModalOpen(false);
    } else {
      message.error("Xəta baş verdi");
    }
  };

  const handleSaveAll = async () => {
    const data = settings
      .filter(
        (item) =>
          !isAddressKey(item.key) &&
          (item.originalValue ?? "") !== (item.value ?? ""),
      )
      .map((item) => ({
        key: item.key,
        value: item.value,
      }));

    if (!data.length) {
      message.info("Yadda saxlamaq üçün dəyişiklik yoxdur.");
      return;
    }

    const response = await SettingsService.updateSettings(data);

    if (response.status === 200) {
      message.success("Dəyişikliklər yadda saxlanıldı");
      setSettings((prev) =>
        prev.map((item) =>
          isAddressKey(item.key)
            ? item
            : { ...item, originalValue: item.value },
        ),
      );
    } else {
      message.error("Xəta baş verdi");
    }
  };

  const handleDelete = async (id: any) => {
    await SettingsService.deleteSettings(id);
    setSettings((prev) => prev.filter((i) => i.id !== id));
    message.success("Parametr silindi");
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Parametrlər</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="page_head">
            <h3 className="page_title">Parametrlər</h3>
          </div>

          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={18} />
            </MainButton>

            <div className={styles.settings_form}>
              {settings.length ? (
                settings.map((item) => (
                  <div key={item.id} className={styles.settings_card}>
                    <label className={styles.label}>{item.key}</label>

                    <div className={styles.input_wrapper}>
                      {isAddressKey(item.key) ? (
                        <input
                          type="text"
                          value="Ünvanı dillərə görə redaktə et"
                          readOnly
                          onClick={() => openAddressModal(item)}
                          style={{ cursor: "pointer" }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={item.value || ""}
                          onChange={(e) =>
                            handleInputChange(item.id, e.target.value)
                          }
                        />
                      )}

                      <Popconfirm
                        title="Silmək istədiyinizə əminsiniz?"
                        onConfirm={() => handleDelete(item.id)}
                        onCancel={() =>
                          message.info("Silinmə əməliyyatı ləğv edildi.")
                        }
                        okText="Bəli"
                        cancelText="Xeyr"
                        placement="topRight"
                      >
                        <div className={styles.delete_icon}>
                          <i className="fa-solid fa-trash"></i>
                        </div>
                      </Popconfirm>
                    </div>
                  </div>
                ))
              ) : (
                <Empty />
              )}
            </div>

            {settings.length > 0 && (
              <MainButton onClick={handleSaveAll}>
                Dəyişiklikləri yadda saxla
              </MainButton>
            )}
          </div>
        </>
      )}

      {/* Ünvan Modal */}
      <Modal
        title="Ünvan – Dil versiyaları"
        open={addressModalOpen}
        onCancel={() => setAddressModalOpen(false)}
        footer={null}
        centered
        className={styles.address_modal}
      >
        <form
          className="modal_form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveAddress();
          }}
        >
          <div className="langs">
            {langs.map((code) => (
              <button
                key={code}
                className={
                  code.toLowerCase() === langCode.toLowerCase()
                    ? "active_lang_btn"
                    : ""
                }
                type="button"
                onClick={() => setLangCode(code)}
              >
                {code === "uz" ? code : code.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="form_input">
            <label className="label">
              Ünvan ({langCode === "uz" ? "uz" : langCode.toUpperCase()})
            </label>
            <div className="input_wrapper">
              <textarea
                rows={4}
                value={
                  translations.find((t) => t.locale === langCode)?.value || ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setTranslations((prev) => {
                    const exists = prev.find((t) => t.locale === langCode);
                    if (exists) {
                      return prev.map((t) =>
                        t.locale === langCode ? { ...t, value } : t,
                      );
                    }
                    return [...prev, { locale: langCode, value }];
                  });
                }}
              />
              <span className="focus_border"></span>
            </div>
          </div>

          <MainButton full type="submit" className="form_add_btn">
            Redaktə edin
          </MainButton>
        </form>
      </Modal>

      {/* Yeni Parametr əlavə et */}
      <Modal
        title={"Yeni Parametr əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="key">
              Parametrin adı
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                id="key"
                name="key"
                placeholder="Parametrin adı"
                value={values.key}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.key} touched={touched.key} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="value">
              Parametrin dəyəri
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                id="value"
                name="value"
                placeholder="Parametrin dəyəri"
                value={values.value}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.value} touched={touched.value} />
          </div>

          <MainButton type="submit" full>
            Əlavə edin
          </MainButton>
        </form>
      </Modal>
    </div>
  );
};

export default Settings;
